/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import axios from 'axios';
import { cloneDeep } from 'lodash-es';
import yaml2json from 'js-yaml';
import { defineStore } from 'pinia';

import { WorkloadsApiService, RegistryApiService } from '@/services/api';
import ComposeWorkloadVersionModel from '@/model/workloads/compose-workload-version.model';
import WorkloadVersionModel from '@/model/workloads/workload-version.model';
import DockerConfigurationStorageModel from '@/model/workloads/docker-configuration-storage.model';
import ComposeServiceModel from '@/model/workloads/compose-service.model';
import ComposeFileModel from '@/model/workloads/compose-file.model';
import RemoteConnectionModel from '@/model/node/remoteConnection.model';
import WorkloadsHelper from '@/store/helpers/workloads.helper';
import i18n from '@/i18n';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import { compareTwoDockerImageNamesAreEqual } from '@/helpers/shared';

const { CancelToken } = axios;
let source = CancelToken.source();

const useWorkloadVersionStore = defineStore('workloadVersion', {
  state: () => ({
    selectedVersion: {},
    originalVersion: {},
    fileContent: '',
    isComposeFileValid: true,
    uploadedComposeFile: {},
    changedDockerImages: {},
    originalFiles: [],
    statusOfImages: [],
    isInternalDockerRegistry: true,
    volumeNameListCompose: {},
    volumeNameList: [],
    selectedVolumeName: '',
    isSaveEnabled: false,
    progress: 0,
    selectedDeployedWorkloadVersionTimestamp: null,
    isTimestampFromActiveRC: false,
    formChangedFlag: false,
  }),

  actions: {
    setProgress(progress) {
      this.progress = progress;
    },
    setComposeVersion(version) {
      this.selectedVersion = new ComposeWorkloadVersionModel({
        version,
      });
    },
    setJsonFileContent({ composeFile, files }) {
      if (typeof composeFile === 'string') {
        composeFile = yaml2json.load(composeFile);
      }
      this.fileContent = composeFile;
      const registries = [];
      const services = [];
      const originalFiles = [];
      const volumeNameListCompose = {};
      const hasFilesParam = Array.isArray(files);
      const responseFiles = files || [];
      const dockerConfigStorages = this.selectedVersion.workloadSpecificProperties?.dockerConfigurationStorage || [];

      const resolveRegistryName = (image) => {
        if (!image) {
          return 'docker.io';
        }
        let registry;
        if (image.includes('/')) {
          const lastSlashIndex = image.lastIndexOf('/');
          registry = image.slice(0, lastSlashIndex);
        } else {
          registry = 'docker.io';
        }
        if (!registry.includes('.')) {
          registry = `docker.io/${registry}`;
        }
        return registry;
      };

      const upsertRegistry = ({ registryName, username, password }) => {
        const existingRegistry = registries.find((registry) => registry.registryName === registryName);
        if (!existingRegistry) {
          registries.push({
            registryName,
            username,
            password,
            textFieldType: 'text',
            hidePassword: true,
          });
          return;
        }
        if (username) {
          existingRegistry.username = username;
        }
      };

      if (composeFile && composeFile.services) {
        const serviceNames = Object.getOwnPropertyNames(composeFile.services).filter((name) => name !== '__ob__');

        serviceNames.forEach((name) => {
          const composeService = composeFile.services[name];
          const image = composeService?.image || '';

          let dockerConfigurationStorage;
          let configStorage = '';
          dockerConfigStorages.forEach((storage) => {
            if (storage.serviceName === name) {
              dockerConfigurationStorage = storage;
              configStorage = storage.volumeName ? `${storage.volumeName}:${storage.containerPath}` : '';
            }
          });
          if (!dockerConfigurationStorage) {
            dockerConfigurationStorage = new DockerConfigurationStorageModel({ serviceName: name });
          }

          const matchedFile = responseFiles.find((file) =>
            compareTwoDockerImageNamesAreEqual(file.source, image, this.isInternalDockerRegistry),
          );
          const composeServiceFile = new ComposeFileModel(matchedFile);

          services.push(
            new ComposeServiceModel({
              serviceName: name,
              dockerFileOption: image,
              ...(image && { dockerFilePath: image }),
              dockerConfigurationStorage,
              configStorage,
              ...(hasFilesParam && { file: composeServiceFile }),
              ...(hasFilesParam && matchedFile && matchedFile.lastError && { errorMessage: matchedFile.lastError }),
            }),
          );

          originalFiles.push({
            serviceName: name,
            file: hasFilesParam ? new ComposeFileModel(matchedFile) : {},
          });

          const registryName = resolveRegistryName(image);
          upsertRegistry({
            registryName,
            username: matchedFile?.username || '',
            password: matchedFile?.password || '',
          });

          const models = WorkloadsHelper.findNamedVolumes(composeService.volumes).map(
            (item) => new DockerConfigurationStorageModel({ ...item, serviceName: name }),
          );
          if (models.length !== 0) {
            models.unshift({
              volumeName: i18n.global.t('workloadVersion.emptyState'),
              serviceName: name,
            });
          }
          volumeNameListCompose[name] = models;
        });
      }

      services.sort((a, b) => {
        const statusOrder = {
          failed: 0,
          available: 1,
          downloading: 2,
          pending: 2,
          cancelled: 2,
        };

        const aStatus = a.file?.status;
        const bStatus = b.file?.status;

        if (statusOrder[aStatus] < statusOrder[bStatus]) {
          return -1;
        }
        if (statusOrder[aStatus] > statusOrder[bStatus]) {
          return 1;
        }
        return 0;
      });

      this.selectedVersion.services = services;
      this.originalFiles = originalFiles;
      this.selectedVersion.registries = registries;
      this.volumeNameListCompose = volumeNameListCompose;
    },
    async verifyUploadedComposeFile(file) {
      const result = await WorkloadsApiService.checkComposeFile(file);
      this.setJsonFileContent({ composeFile: result });
    },
    saveUploadedComposeFile(file) {
      this.uploadedComposeFile = file;
    },
    verifyUploadedComposeFileFailed() {
      this.isComposeFileValid = false;
      this.saveUploadedComposeFile({});
      this.setJsonFileContent({ composeFile: '' });
    },
    setDockerFiles(file) {
      const path = file?.source.replace('registry/', '') || '';
      this.selectedVersion.dockerFilePath = path;
      this.originalFiles.push(file);
    },
    async getFiles(params = {}) {
      const { workloadId, versionId } = params;
      if (!workloadId || !versionId) return [];

      const { files = [] } = await WorkloadsApiService.fetchFiles({
        wlId: workloadId,
        verId: versionId,
      });

      const composeMeta = files.find((file) => file.type === 'compose');
      const composeFile = composeMeta?._id
        ? await WorkloadsApiService.fetchComposeFile({
            wlId: workloadId,
            verId: versionId,
            fileId: composeMeta._id,
          })
        : '';

      this.setJsonFileContent({ composeFile, files });

      const nextChanged = {};
      const previous = this.changedDockerImages || {};
      const resolveExistingKey = (source) =>
        Object.keys(previous).find((saved) => compareTwoDockerImageNamesAreEqual(saved, source));

      for (const file of files) {
        if (file.type !== 'docker-image' || file.status === 'available' || !file.source) continue;

        const key = resolveExistingKey(file.source) || file.source;
        const prev = previous[key] || {};

        nextChanged[key] = {
          ...prev,
          file,
          username: file.username,
          password: file.password,
          source: file.source,
          origin: file.origin,
          status: file.status,
          _id: file._id,
        };
      }

      this.changedDockerImages = nextChanged;
      return files;
    },
    setFiles(files) {
      files.forEach((file) => {
        if (file.type === 'compose') {
          this.selectedVersion.composeFile.originalName = file.originalName;
          this.selectedVersion.composeFile._id = file._id;
        }
      });
    },
    async getComposeVersionById(params) {
      const { id, workloadId, isInternalDockerRegistry, versions, isUpdate, isClone, hasCreatePermission } = params;
      this.isInternalDockerRegistry = isInternalDockerRegistry;
      if (!isUpdate) {
        if (!versions.length) {
          this.setComposeVersion({});
          this.originalVersion = cloneDeep(this.selectedVersion);
          return;
        }
        if (versions.length && workloadId) {
          const version = await WorkloadsApiService.fetchWorkloadVersionByIdUsingV3Api({
            verId: isClone ? id : versions[versions.length - 1].id,
            wlId: workloadId,
          });
          if (hasCreatePermission) {
            version.remoteConnections.forEach((rc) => {
              delete rc.id;
              delete rc._id;
              delete rc.workloadId;
            });
          } else {
            version.remoteConnections = [];
          }
          this.setComposeVersion(version);
          const files = await this.getFiles({
            versionId: isClone ? id : versions[versions.length - 1].id,
            workloadId: workloadId,
          });
          this.setFiles(files);
          this.originalVersion = cloneDeep(this.selectedVersion);
          return;
        }
      }
      const version = await WorkloadsApiService.fetchWorkloadVersionByIdUsingV3Api({
        verId: id,
        wlId: workloadId,
      });
      this.setComposeVersion(version);
      const files = await this.getFiles({
        versionId: id,
        workloadId: workloadId,
      });
      this.setFiles(files);
      files.find((file) => {
        if (file.status === 'failed') {
          this.selectedVersion.errorMessage = file.lastError;
        }
      });
      this.originalVersion = cloneDeep(this.selectedVersion);
    },
    setComposeFileFlag(value) {
      this.isComposeFileValid = value;
    },
    removeChangedDockerImages({ imageName, removeAll }) {
      if (removeAll) {
        this.changedDockerImages = {};
        return;
      }
      delete this.changedDockerImages[imageName];
    },
    saveChangedDockerImages({ file, imageName, changedFile }) {
      const key =
        Object.keys(this.changedDockerImages)?.find((image) => compareTwoDockerImageNamesAreEqual(image, imageName)) ||
        imageName;
      this.changedDockerImages[key] = {
        ...(file && { file }),
        username: changedFile.username,
        password: changedFile.password,
        source: changedFile.source,
        origin: changedFile.origin,
        status: changedFile.status,
        _id: changedFile._id,
      };
    },
    saveChangedDockerImagesSpecificProperty({ imageName, property, value }) {
      const key =
        Object.keys(this.changedDockerImages)?.find((image) => compareTwoDockerImageNamesAreEqual(image, imageName)) ||
        imageName;
      if (!this.changedDockerImages[key]) {
        this.changedDockerImages[key] = {};
      }
      this.changedDockerImages[key][property] = value;
    },
    async defineAllFiles(param) {
      const response = await WorkloadsApiService.defineAllFiles(param);
      return response.files;
    },
    uploadFile(param) {
      return WorkloadsApiService.addFile(param);
    },
    changeFile(param) {
      return WorkloadsApiService.editFile(param);
    },
    async createWorkloadVersionUsingV3Api(payload) {
      source = CancelToken.source();
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      const version = await WorkloadsApiService.createWorkloadVersionUsingV3Api({
        payload,
        token: source.token,
        cb: (progress) => this.setProgress(progress),
      });
      return version;
    },
    async updateWorkloadVersionUsingV3Api(payload) {
      const { data, dockerFilePath, fullDockerFilePath, verId, wlId } = payload;
      source = CancelToken.source();
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      const version = await WorkloadsApiService.updateWorkloadVersionUsingV3Api({
        payload: { data, verId, wlId },
        token: source.token,
        cb: (progress) => this.setProgress(progress),
      });
      const originalFileEntry =
        this.originalFiles.find((entry) => entry?.originalName || entry?.file?.originalName) || this.originalFiles[0];
      const originalName = originalFileEntry?.originalName || originalFileEntry?.file?.originalName;
      const fileId = originalFileEntry?._id || originalFileEntry?.file?._id;
      if (dockerFilePath && fullDockerFilePath && originalName && fileId) {
        const isFileChanged = WorkloadsHelper.checkIfFileHasChanged(dockerFilePath, originalName);
        if (isFileChanged) {
          const form = new FormData();
          form.append('type', 'docker-image');
          form.append('origin', 'docker-repo');
          form.append('source', fullDockerFilePath);
          this.changeFile({
            wlId,
            verId,
            fileId,
            data: form,
            origin: 'docker-repo',
          });
        }
      }
      utilsStore.showCustomToast({
        text: 'workloadDetail.successfullyUpdate',
        color: 'green',
        showClose: true,
      });
      return version;
    },
    setDockerFromRegistryVersion(version) {
      this.volumeNameList = [];
      this.selectedVolumeName = '';
      this.selectedVersion = new WorkloadVersionModel({
        version: version,
        type: 'docker',
      });
      if (this.selectedVersion.workloadProperties && this.selectedVersion.workloadProperties.docker_volumes) {
        this.volumeNameList.push(i18n.global.t('workloadVersion.emptyState'));
        const seenVolumeNames = new Set();
        this.selectedVersion.workloadProperties.docker_volumes.forEach((volume) => {
          if (volume.volumeName && !seenVolumeNames.has(volume.volumeName)) {
            seenVolumeNames.add(volume.volumeName);
            this.volumeNameList.push(volume.volumeName);
          }
          if (volume.configurationStorage === true) {
            this.selectedVolumeName = volume.volumeName;
          }
        });
        return;
      }
      this.selectedVolumeName = '';
    },
    async getDockerFromRegistryVersionById(params) {
      const { id, workloadId, versions, isUpdate, isClone, hasCreatePermission } = params;
      this.isInternalDockerRegistry = true;
      if (!isUpdate) {
        if (!versions.length) {
          this.setDockerFromRegistryVersion({});
          this.originalVersion = cloneDeep(this.selectedVersion);
          return;
        }
        if (versions.length && workloadId) {
          const version = await WorkloadsApiService.fetchWorkloadVersionByIdUsingV3Api({
            verId: isClone ? id : versions[versions.length - 1]._id,
            wlId: workloadId,
          });
          if (hasCreatePermission) {
            version.remoteConnections.forEach((rc) => {
              delete rc.id;
              delete rc._id;
            });
          } else {
            version.remoteConnections = [];
          }
          this.setDockerFromRegistryVersion(version);
          const files = await this.getFiles({
            versionId: isClone ? id : versions[versions.length - 1]._id,
            workloadId: workloadId,
          });
          this.setDockerFiles(files[0]);
          this.originalVersion = cloneDeep(this.selectedVersion);
          return;
        }
      }
      const version = await WorkloadsApiService.fetchWorkloadVersionByIdUsingV3Api({
        verId: id,
        wlId: workloadId,
      });
      this.setDockerFromRegistryVersion(version);
      const files = await this.getFiles({
        versionId: id,
        workloadId: workloadId,
      });
      this.setDockerFiles(files[0]);
      files.find((file) => {
        if (file.status === 'failed') {
          this.selectedVersion.errorMessage = file.lastError;
        }
      });
      this.originalVersion = cloneDeep(this.selectedVersion);
    },
    disableSaveButton() {
      this.isSaveEnabled = false;
    },
    setSaveEnabled(currentVersion) {
      this.isSaveEnabled = WorkloadsHelper.shouldSaveBtnBeEnabled({
        currentVersion,
        originalVersion: this.originalVersion,
      });
    },
    clearOriginalFiles() {
      this.originalFiles = [];
    },
    cancelRequest() {
      source.cancel();
    },

    setSelectedDeployedWorkloadVersionTimestamp({ dateString, isActiveRc }) {
      let timestamp = null;
      if (dateString) {
        // Split the date and time parts
        const [datePart, timePart] = dateString.split(', ');
        // Split the date into day, month, and year
        const [day, month, year] = datePart.split('/').map(Number);
        // Split the time into hours, minutes, and seconds
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        // Create a new Date object using the parsed values
        const date = new Date(year, month - 1, day, hours, minutes, seconds);
        timestamp = date.getTime();
      }
      this.selectedDeployedWorkloadVersionTimestamp = timestamp;
      this.isTimestampFromActiveRC = isActiveRc;
    },
    updateAffectedVersionsAndFiles({ params, fileStatus }) {
      this.selectedVersion?.services?.map((service) => {
        if (
          (service.file && service.file.origin === 'docker-repo' && service.file.originalName === params.imageName) ||
          (service.dockerFilePath.endsWith(params.imageName) && service.dockerFileOption.endsWith(params.imageName))
        ) {
          service.file.status = fileStatus;
          const statuses = this.statusOfImages.map((statusOfImage) => {
            if (statusOfImage.image === params.imageName) {
              statusOfImage.available = fileStatus === 'available';
            }
            return statusOfImage;
          });

          this.statusOfImages = statuses;
        }
        return service;
      });
    },
    handleDockerRegistryEvent(payload) {
      const { name, params } = payload;
      switch (name) {
        case 'image_pushed_to_registry':
          this.updateAffectedVersionsAndFiles({ params, fileStatus: 'available' });
          break;
        case 'image_deleted_from_registry':
          this.updateAffectedVersionsAndFiles({ params, fileStatus: 'pending' });
          break;
      }
    },
    async checkStatusOfImages(params) {
      const response = await RegistryApiService.checkStatusOfImages(params);
      this.statusOfImages = response.listOfImagesWithStatus;
    },
    removeRCFromVersion(params) {
      this.selectedVersion.remoteConnections.splice(params.index, 1);
    },
    removeRc() {
      this.selectedVersion.remoteConnections = [];
    },
    addEditRcFromWorkload({ connection }) {
      // rc already exist -> edit the existing rc
      if (connection.index !== undefined) {
        this.selectedVersion.remoteConnections.splice(connection.index, 1, new RemoteConnectionModel(connection));
        return;
      }
      this.selectedVersion.remoteConnections.push(new RemoteConnectionModel(connection));
    },
    formHasChanged() {
      this.formChangedFlag = !this.formChangedFlag;
    },
  },
});

export default useWorkloadVersionStore;
